import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver-es';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, retry, shareReplay } from 'rxjs/operators';

import { MissionListDataModel } from '../models/mission-list-data.model';
import { MissionOverviewDataModel } from '../models/mission-overview-data.model';
import { MissionModel } from '../models/mission.model';

const API_ENDPOINT = '/api/mission/';

@Injectable({
  providedIn: 'root',
})
export class MissionService {

  public missionListSlideIndex: BehaviorSubject<number> = new BehaviorSubject(null);

  private missionOverviewData: Observable<MissionOverviewDataModel>;
  private missionListData: Observable<MissionListDataModel>;

  /**
   * Constructor
   *
   * @param http
   */
  public constructor(private http: HttpClient) {
  }

  /**
   * Gets an observable that resolves to the current set mission data
   *
   * @return {Observable<MissionOverviewDataModel>}
   */
  public getMissionOverviewData(): Observable<MissionOverviewDataModel> {
    if (!this.missionOverviewData) {
      this.missionOverviewData = this.loadMissionOverviewData().pipe(
        shareReplay(1),
      );
    }

    return this.missionOverviewData;
  }

  /**
   * Gets an observable that resolves to the current set mission data
   *
   * @return {Observable<MissionOverviewDataModel>}
   */
  public getMissionListData(): Observable<MissionListDataModel> {
    if (!this.missionListData) {
      this.missionListData = this.loadMissionListData().pipe(
        shareReplay(1),
      );
    }

    return this.missionListData;
  }

  /**
   * Clear caching because missions need to be reloaded.
   *
   * @return void
   */
  public clearMissionData(): void {
    this.clearMissionOverviewData();
    this.clearMissionListData();
  }

  /**
   * Clear caching because missions need to be reloaded.
   *
   * @return void
   */
  public clearMissionListData(): void {
    this.missionListData = null;
  }

  /**
   * Clear caching because missions need to be reloaded.
   *
   * @return void
   */
  public clearMissionOverviewData(): void {
    this.missionOverviewData = null;
  }

  /**
   * Gets a list of game periods and the active game period
   *
   * @return Observable<any>
   */
  public loadMissionOverviewData(): Observable<any> {
    return this.http.get(API_ENDPOINT + 'get/list')
    .pipe(
      retry(3),
      map((response: { items: MissionOverviewDataModel }) => response.items),
    );
  }

  /**
   * Gets a list of game periods and the active game period
   *
   * @return Observable<any>
   */
  public loadMissionListData(): Observable<any> {
    return this.http.get(API_ENDPOINT + 'get/mission-list')
      .pipe(
        retry(3),
        map((response: { items: MissionListDataModel }) => response.items),
      );
  }

  /**
   * Gets a single mission
   *
   * @param iHash
   *
   * @return Observable<MissionModel>
   */
  public getSingle(iHash: string): Observable<MissionModel> {
    return this.http.get<MissionModel>(API_ENDPOINT + 'get/single/' + iHash).pipe(
      map((response: any) => {
        return response.items.mission;
      }),
      catchError((error: Error) => {
        return throwError(() => error);
      }),
    );
  }

  /**
   * Request for downloading a file
   *
   * @param file
   *
   * @return void
   */
  public getDownloadFile(file: any): void {
    this.http.get(file.link, {responseType: 'blob'}).subscribe(
      (res: Blob) => {
        saveAs(res, file.name);
      },
      (error: Error) => {
        throwError(error);
      },
    );
  }
}
